import * as amplitude from '@amplitude/analytics-browser';
import MobileDetect from 'mobile-detect';

import { LogEventArgs, LogPurchaseArgs } from '@/hooks/analytics/types';

import { calmLogger } from '../calmLogger';

export type LogEventResult = amplitude.Types.Result;

export function setAmplitudeUserId(userId: string): void {
	if (userId) {
		amplitude.setUserId(userId);
	}
}

export function setAmplitudeDeviceProperties(deviceId: string): void {
	if (deviceId) {
		amplitude.setDeviceId(deviceId);
		const md = new MobileDetect(window.navigator.userAgent);
		const deviceType = md.mobile() ? 'mobile' : 'desktop';

		const identifyObj = new amplitude.Identify();
		identifyObj.set('web_device_type', deviceType);
		identifyObj.set('Other', deviceId);
		amplitude.identify(identifyObj);
	}
}

export function setAmplitudeUserProperties(
	userProperties: Record<string, amplitude.Types.ValidPropertyType>,
): void {
	const identifyObj = new amplitude.Identify();
	Object.entries(userProperties).forEach(([key, value]) => {
		identifyObj.set(key, value);
	});
	amplitude.identify(identifyObj);
}

// Written by the SDK's page-URL enrichment plugin, which this app disables. Named here only
// so a value left behind by a build that had it enabled can be cleared.
const AMP_URL_INFO_STORAGE_KEY = 'AMP_URL_INFO';

// Both spellings, and with no leading slash required: the route spells it `password-reset`,
// while the backend endpoint is `password_reset/<token>` — and useApi logs that endpoint
// string verbatim as an event property when PerimeterX challenges the request. The captured
// group puts back whichever spelling matched.
const PASSWORD_RESET_PATH_PATTERN = /(password[-_]reset)\/.+/;
const REDACTED_PASSWORD_RESET_PATH = '$1/REDACTED';

// Recurses because event properties are arbitrary JSON, so a URL can sit inside a nested
// object or array rather than at the top level.
const redactPasswordResetPath = (value: unknown): unknown => {
	if (typeof value === 'string') {
		return value.replace(PASSWORD_RESET_PATH_PATTERN, REDACTED_PASSWORD_RESET_PATH);
	}

	if (Array.isArray(value)) {
		return value.map(redactPasswordResetPath);
	}

	if (typeof value === 'object' && value !== null) {
		return Object.fromEntries(
			Object.entries(value).map(([key, nested]) => [key, redactPasswordResetPath(nested)]),
		);
	}

	return value;
};

// Values are rewritten but the shape is not, so the cast restores the caller's type rather
// than widening every event-prop signature to unknown.
const redactEventProps = <T extends object>(eventProps: T): T =>
	Object.fromEntries(
		Object.entries(eventProps).map(([key, value]) => [key, redactPasswordResetPath(value)]),
	) as T;

// The /password-reset/<token> route carries a single-use credential in its path, which must
// never reach a third party. Matching on the value rather than on a list of property names is
// deliberate: the SDK stamps page-URL properties onto every event and has both added and
// renamed them across versions, so an allowlist of names quietly stops covering whatever the
// next upgrade introduces.
// we can remove this once we are no longer using the /password-reset/token route
// we can probably remove this once we are migrated to Auth0
const urlSanitizationEnrichment = (): amplitude.Types.EnrichmentPlugin => {
	return {
		name: 'url-sanitization-enrichment',
		type: 'enrichment',
		setup: async () => undefined,
		execute: async event => {
			if (!event.event_properties) {
				return event;
			}

			// Mapped over entries rather than indexed by key: event_properties is a union that
			// includes the revenue shape, which has no string index signature.
			event.event_properties = Object.fromEntries(
				Object.entries(event.event_properties).map(([property, value]) => [
					property,
					redactPasswordResetPath(value),
				]),
			);

			return event;
		},
	};
};

export async function initAmplitude(
	amplitudeToken = process.env.NEXT_PUBLIC_AMPLITUDE_TOKEN,
	deviceId?: string,
): Promise<void> {
	// Disabling the page-URL plugin stops it writing this, but cannot unwrite what a build
	// running with it enabled already stored, and sessionStorage outlives reloads within a tab.
	// Clearing on every init means "no credential in storage" holds for a tab that saw such a
	// build too, rather than only for one that never did.
	try {
		window.sessionStorage.removeItem(AMP_URL_INFO_STORAGE_KEY);
	} catch {
		// Storage access throws outright when the browser blocks site data.
	}

	if (!amplitudeToken) {
		calmLogger.warn('NEXT_PUBLIC_AMPLITUDE_TOKEN not defined.. expect errors below');
		return;
	}

	await amplitude.init(amplitudeToken, undefined, {
		defaultTracking: true,
		// Off because this plugin mirrors the current and previous href into sessionStorage
		// (AMP_URL_INFO) as a side effect, outside the event pipeline any redaction can reach —
		// which would leave the /password-reset/<token> credential readable by same-origin
		// scripts for a navigation or two after leaving that route.
		//
		// Naming any autocapture key replaces defaultTracking wholesale, but every key absent
		// from this object still defaults to enabled, so attribution, file downloads, form
		// interactions, page views and sessions all stay on.
		autocapture: {
			pageUrlEnrichment: false,
		},
		// Remote config would make the opt-out above advisory rather than binding: the SDK
		// spreads a remotely-supplied autocapture object over the local one, so its fields win
		// and a remote pageUrlEnrichment:true would silently restore the credential-in-storage
		// behaviour. The SDK guards a locally-explicit false for customEnrichment and
		// deliberately does not for autocapture, so the only way to hold this setting is to stop
		// fetching. Also keeps the SDK from calling out on every init, as 2.2.3 never did.
		fetchRemoteConfig: false,
		cookieOptions: {
			domain: 'calm.com',
		},
		deviceId,
	}).promise;

	// Must stay after init: enrichment plugins run in registration order, and registering
	// before init puts this ahead of the SDK's own page-URL enrichment, which would then
	// append unredacted URLs after this plugin had already run. Awaited so that an event
	// tracked immediately after this function resolves cannot bypass the sanitizer.
	await amplitude.add(urlSanitizationEnrichment()).promise;
}

export async function logAmplitudeEvent({
	eventName,
	eventProps,
}: LogEventArgs): Promise<amplitude.Types.Result> {
	// Redacted here as well as in the enrichment plugin. Callers can log before init — the
	// analytics context exposes logEvent without gating on initialisation — and whether the
	// SDK's queue for those calls drains before or after a plugin registered post-init is an
	// internal detail, not a documented contract. Measured today it drains after, so the plugin
	// alone does cover them; redacting in our own call means that staying true is not load
	// bearing. Only app-originated events reach here, so the plugin is still what covers the
	// page views and session events the SDK raises itself.
	const res = await amplitude.track(eventName, eventProps && redactEventProps(eventProps)).promise;
	return res;
}

export async function logAmplitudePurchase({
	productInfo,
	transactionInfo,
}: LogPurchaseArgs): Promise<amplitude.Types.Result> {
	const revenueEvent = new amplitude.Revenue()
		.setProductId(productInfo.name)
		.setPrice(productInfo.unit_price)
		.setQuantity(productInfo.quantity)
		.setRevenueType(productInfo.sku || productInfo.name);

	if (transactionInfo.id) {
		revenueEvent.setEventProperties({ id: transactionInfo.id });
	}

	const res = await amplitude.revenue(revenueEvent).promise;
	return res;
}
